import { useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull, notNull } from "@msys/common";
import {
  Autocomplete,
  ButtonSelect,
  ButtonSelectMultiple,
  getFormattedNumber,
  Select,
  SelectMultiple,
  useDebouncedValue,
} from "@msys/ui";
import { Typography } from "@mui/material";
import type { TFunction } from "@msys/tolgee";
import { useTolgee, useTranslate } from "@tolgee/react";
import { FormikProps } from "formik";
import { isNil, pick } from "lodash-es";
import { matchSorter } from "match-sorter";
import React from "react";
import * as Yup from "yup";
import {
  ProjectType,
  TemplateSearchFilterPropertyFilterInput,
  TypeOfTemplate,
} from "../../../../clients/graphqlTypes.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import { useTypeOfTemplates } from "../quote/useTypeOfTemplates.js";
import {
  TemplateFilterFields_AvailableFiltersQueryVariables,
  useTemplateFilterFields_AvailableFiltersQuery,
} from "./TemplateFilterFields.generated.js";

type TemplateProperty =
  | {
      key: string;
      label: string;
      values: { count: number; value: boolean }[];
      type: "bool";
    }
  | {
      key: string;
      label: string;
      values: { count: number; value: number }[];
      type: "number";
    }
  | {
      key: string;
      label: string;
      values: { count: number; value: number }[];
      type: "number_array";
    }
  | {
      key: string;
      label: string;
      values: { count: number; value: string; label: string }[];
      type: "text";
    }
  | {
      key: string;
      label: string;
      values: { count: number; value: string; label: string }[];
      type: "text_array";
    };

export type TemplateFiltersFormValues = {
  typeOfTemplates: TypeOfTemplate[] | null;
  typeOfProjects: ProjectType[] | null;
  categories: string[] | null;
  templateTypeIds: string[] | null;
  properties: TemplateSearchFilterPropertyFilterInput[] | null;
};

type FiltersViewType = "inputs" | "buttons";

interface Props {
  templateSearchVariables: TemplateFilterFields_AvailableFiltersQueryVariables;
  formikProps: FormikProps<TemplateFiltersFormValues>;
  hideTemplateTypes?: boolean;
  hideCategories?: boolean;
  hideTypeOfProjects?: boolean;
  hideTypeOfTemplates?: boolean;
  showOnlyPropertyFiltersWithValues?: boolean;
  viewType?: FiltersViewType;
  fixedFilters?: Partial<TemplateFiltersFormValues>;
}

export function useTemplateFilterFields({
  templateSearchVariables,
  formikProps,
  hideTemplateTypes,
  hideCategories,
  hideTypeOfProjects,
  hideTypeOfTemplates,
  showOnlyPropertyFiltersWithValues,
  viewType = "inputs",
  fixedFilters,
}: Props): {
  nonPropertyFields: React.ReactElement[];
  propertyFields: React.ReactElement[];
  isLoading: boolean;
} {
  const { t } = useTranslate(["Global", "TemplatesSearch", "Templates"]);
  const language = useTolgee(["language"]).getLanguage()!;

  const { projectTypeLabels } = useProjectTypes();
  const { typeOfTemplatesLabels } = useTypeOfTemplates();

  const { values, setFieldValue } = formikProps;

  const debouncedValues = useDebouncedValue(values, 500);

  const variables = React.useMemo(
    () => ({
      ...templateSearchVariables,
      filters: {
        ...templateSearchVariables.filters,
        ...getTemplateFormValues(debouncedValues),
      },
    }),
    [templateSearchVariables, debouncedValues]
  );

  const client = useApolloClient();
  const availableFiltersQuery = useTemplateFilterFields_AvailableFiltersQuery({
    client,
    variables,
  });
  const availableFilters = getDataOrNull(
    (availableFiltersQuery.data ?? availableFiltersQuery.previousData)
      ?.searchTemplatesAvailableFilters
  );

  const templateTypeOptions = React.useMemo(() => {
    const templateTypeOptions = availableFilters?.templateTypeIds?.map(pt => ({
      value: pt.id,
      label: `${pt.templateType.title} (${pt.templateType.key})`,
      counter: pt.count,
    }));
    const templateTypeIds = templateTypeOptions?.map(pt => pt.value) ?? [];
    const templateTypeCounters =
      templateTypeOptions?.reduce(
        (acc, pt) => {
          acc[pt.value] = pt.counter;
          return acc;
        },
        {} as Record<string, number>
      ) ?? {};
    const templateTypeLabels =
      templateTypeOptions?.reduce(
        (acc, pt) => {
          acc[pt.value] = pt.label;
          return acc;
        },
        {} as Record<string, string>
      ) ?? {};
    const filterTemplateTypeOptions = (
      options: { value: string; label: string }[],
      { inputValue }: { inputValue: string }
    ) =>
      matchSorter(options, inputValue, { keys: ["label"] }).sort(
        (o1, o2) =>
          (templateTypeCounters[o2.value] ?? 0) -
          (templateTypeCounters[o1.value] ?? 0)
      );
    const filterTemplateTypeStringOptions = (
      options: string[],
      { inputValue }: { inputValue: string }
    ) =>
      filterTemplateTypeOptions(
        options.map(o => ({ value: o, label: templateTypeLabels[o] })),
        { inputValue }
      ).map(o => o.value);
    return {
      templateTypeIds,
      templateTypeCounters,
      templateTypeLabels,
      filterTemplateTypeOptions,
      filterTemplateTypeStringOptions,
    };
  }, [availableFilters?.templateTypeIds]);

  const categoryOptions = React.useMemo(() => {
    const categories = availableFilters?.categories?.map(c => ({
      value: c.value.id,
      label: c.value.name,
      counter: c.count,
    }));
    const categoryIds = categories?.map(pt => pt.value) ?? [];
    const categoryCounters =
      categories?.reduce(
        (acc, pt) => {
          acc[pt.value] = pt.counter;
          return acc;
        },
        {} as Record<string, number>
      ) ?? {};
    const categoryLabels =
      categories?.reduce(
        (acc, pt) => {
          acc[pt.value] = pt.label;
          return acc;
        },
        {} as Record<string, string>
      ) ?? {};
    const filterCategoryOptions = (
      options: { value: string; label: string }[],
      { inputValue }: { inputValue: string }
    ) =>
      matchSorter(options, inputValue, { keys: ["label"] }).sort(
        (o1, o2) =>
          (categoryCounters[o2.value] ?? 0) - (categoryCounters[o1.value] ?? 0)
      );
    const filterCategoryStringOptions = (
      options: string[],
      { inputValue }: { inputValue: string }
    ) =>
      filterCategoryOptions(
        options.map(o => ({ value: o, label: categoryLabels[o] })),
        { inputValue }
      ).map(o => o.value);
    return {
      categoryIds,
      categoryCounters,
      categoryLabels,
      filterCategoryOptions,
      filterCategoryStringOptions,
    };
  }, [availableFilters?.categories]);

  const typeOfProjectOptions = React.useMemo(() => {
    const typeOfProjects = availableFilters?.typeOfProjects?.map(c => ({
      value: c.value,
      label: projectTypeLabels[c.value],
      counter: c.count,
    }));
    const typeOfProjectValues = typeOfProjects?.map(t => t.value) ?? [];
    const typeOfProjectCounters =
      typeOfProjects?.reduce(
        (acc, pt) => {
          acc[pt.value] = pt.counter;
          return acc;
        },
        {} as Record<string, number>
      ) ?? {};
    const typeOfProjectLabels =
      typeOfProjects?.reduce(
        (acc, pt) => {
          acc[pt.value] = pt.label;
          return acc;
        },
        {} as Record<string, string>
      ) ?? {};
    const filterTypeOfProjectOptions = (
      options: { value: ProjectType; label: string }[],
      { inputValue }: { inputValue: string }
    ) =>
      matchSorter(options, inputValue, { keys: ["label"] }).sort(
        (o1, o2) =>
          (typeOfProjectCounters[o2.value] ?? 0) -
          (typeOfProjectCounters[o1.value] ?? 0)
      );
    const filterTypeOfProjectStringOptions = (
      options: ProjectType[],
      { inputValue }: { inputValue: string }
    ) =>
      filterTypeOfProjectOptions(
        options.map(o => ({ value: o, label: typeOfProjectLabels[o] })),
        { inputValue }
      ).map(o => o.value);
    return {
      typeOfProjectValues,
      typeOfProjectCounters,
      typeOfProjectLabels,
      filterTypeOfProjectOptions,
      filterTypeOfProjectStringOptions,
    };
  }, [availableFilters?.typeOfProjects, projectTypeLabels]);

  const typeOfTemplateOptions = React.useMemo(() => {
    const typeOfTemplates = availableFilters?.typeOfTemplates?.map(c => ({
      value: c.value,
      label: typeOfTemplatesLabels[c.value],
      counter: c.count,
    }));
    const typeOfTemplateValues = typeOfTemplates?.map(t => t.value) ?? [];
    const typeOfTemplateCounters =
      typeOfTemplates?.reduce(
        (acc, pt) => {
          acc[pt.value] = pt.counter;
          return acc;
        },
        {} as Record<string, number>
      ) ?? {};
    const typeOfTemplateLabels =
      typeOfTemplates?.reduce(
        (acc, pt) => {
          acc[pt.value] = pt.label;
          return acc;
        },
        {} as Record<string, string>
      ) ?? {};
    const filterTypeOfTemplateOptions = (
      options: { value: TypeOfTemplate; label: string }[],
      { inputValue }: { inputValue: string }
    ) =>
      matchSorter(options, inputValue, { keys: ["label"] }).sort(
        (o1, o2) =>
          (typeOfTemplateCounters[o2.value] ?? 0) -
          (typeOfTemplateCounters[o1.value] ?? 0)
      );
    const filterTypeOfTemplateStringOptions = (
      options: TypeOfTemplate[],
      { inputValue }: { inputValue: string }
    ) =>
      filterTypeOfTemplateOptions(
        options.map(o => ({ value: o, label: typeOfTemplateLabels[o] })),
        { inputValue }
      ).map(o => o.value);
    return {
      typeOfTemplateValues,
      typeOfTemplateCounters,
      typeOfTemplateLabels,
      filterTypeOfTemplateOptions,
      filterTypeOfTemplateStringOptions,
    };
  }, [availableFilters?.typeOfTemplates, typeOfTemplatesLabels]);

  const propertiesOptions = React.useMemo(() => {
    const properties =
      availableFilters?.propertiesBool &&
      availableFilters?.propertiesNumber &&
      availableFilters?.propertiesNumberArray &&
      availableFilters?.propertiesText &&
      availableFilters?.propertiesTextArray
        ? ([
            ...availableFilters.propertiesBool.map(p => ({
              ...p,
              type: "bool",
            })),
            ...availableFilters.propertiesNumber.map(p => ({
              ...p,
              type: "number",
            })),
            ...availableFilters.propertiesNumberArray.map(p => ({
              ...p,
              type: "number_array",
            })),
            ...availableFilters.propertiesText.map(p => ({
              ...p,
              type: "text",
            })),
            ...availableFilters.propertiesTextArray.map(p => ({
              ...p,
              type: "text_array",
            })),
          ].sort((p1, p2) =>
            p1.key.localeCompare(p2.key)
          ) as TemplateProperty[])
        : undefined;

    function filterProperties(property: TemplateProperty) {
      return templateSearchVariables.filters?.properties?.some(
        filter => filter.key === property.key
      );
    }

    return {
      properties: showOnlyPropertyFiltersWithValues
        ? (properties?.filter(filterProperties) ?? [])
        : (properties ?? []),
    };
  }, [
    availableFilters?.propertiesBool,
    availableFilters?.propertiesNumber,
    availableFilters?.propertiesNumberArray,
    availableFilters?.propertiesText,
    availableFilters?.propertiesTextArray,
    showOnlyPropertyFiltersWithValues,
    templateSearchVariables.filters?.properties,
  ]);

  const nonPropertyFields = React.useMemo(
    (): React.ReactElement[] =>
      [
        (!hideTemplateTypes ||
          (values.templateTypeIds && values.templateTypeIds.length > 0)) &&
        templateTypeOptions.templateTypeIds &&
        templateTypeOptions.templateTypeIds.length > 0 ? (
          viewType === "inputs" ? (
            <Autocomplete
              key="templateTypeIds-input"
              inputLabel={t("Template type", { ns: "TemplatesSearch" })}
              options={templateTypeOptions?.templateTypeIds ?? []}
              getOptionLabel={option =>
                templateTypeOptions?.templateTypeLabels[option] ?? ""
              }
              value={values.templateTypeIds?.[0] ?? null}
              onChange={async newValue => {
                await setFieldValue(
                  "templateTypeIds",
                  newValue ? [newValue] : null
                );
              }}
              filterOptions={
                templateTypeOptions?.filterTemplateTypeStringOptions
              }
              renderOption={(props, option, { selected }) => (
                <li
                  {...props}
                  style={{
                    ...props.style,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>
                    {templateTypeOptions.templateTypeLabels[option] ?? ""}
                  </span>
                  {templateTypeOptions.templateTypeCounters[option] !==
                  undefined ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "grey.600", ml: 1 }}
                    >
                      {getFormattedNumber(
                        templateTypeOptions.templateTypeCounters[option],
                        language
                      )}
                    </Typography>
                  ) : null}
                </li>
              )}
              disabled={!isNil(fixedFilters?.templateTypeIds)}
            />
          ) : viewType === "buttons" ? (
            <ButtonSelect
              key="templateTypeIds-button"
              label={t("Template type", { ns: "TemplatesSearch" })}
              searchLabel={t("Search", { ns: "Global" })}
              noOptionsLabel={t("There are no items to display", {
                ns: "Global",
              })}
              options={(templateTypeOptions.templateTypeIds ?? []).map(id => ({
                value: id,
                label: templateTypeOptions.templateTypeLabels[id] ?? "",
                counter: templateTypeOptions.templateTypeCounters[id],
              }))}
              value={values.templateTypeIds?.[0] ?? null}
              onChange={async newValue => {
                await setFieldValue(
                  "templateTypeIds",
                  newValue ? [newValue] : null
                );
              }}
              filterOptions={templateTypeOptions?.filterTemplateTypeOptions}
              disabled={!isNil(fixedFilters?.templateTypeIds)}
              popoverWidth="xl"
              clearable
            />
          ) : null
        ) : null,
        (!hideCategories ||
          (values.categories && values.categories.length > 0)) &&
        categoryOptions.categoryIds &&
        categoryOptions.categoryIds.length > 0 ? (
          viewType === "inputs" ? (
            <Autocomplete
              key="categories-input"
              inputLabel={t("Template category", { ns: "Templates" })}
              options={categoryOptions?.categoryIds ?? []}
              getOptionLabel={option =>
                categoryOptions?.categoryLabels[option] ?? ""
              }
              value={values.categories?.[0] ?? null}
              onChange={async newValue => {
                await setFieldValue("categories", newValue ? [newValue] : null);
              }}
              filterOptions={categoryOptions?.filterCategoryStringOptions}
              renderOption={(props, option, { selected }) => (
                <li
                  {...props}
                  style={{
                    ...props.style,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{categoryOptions.categoryLabels[option] ?? ""}</span>
                  {categoryOptions.categoryCounters[option] !== undefined ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "grey.600", ml: 1 }}
                    >
                      {getFormattedNumber(
                        categoryOptions.categoryCounters[option],
                        language
                      )}
                    </Typography>
                  ) : null}
                </li>
              )}
              disabled={!isNil(fixedFilters?.categories)}
            />
          ) : viewType === "buttons" ? (
            <ButtonSelect
              key="categories-button"
              label={t("Template category", { ns: "Templates" })}
              searchLabel={t("Search", { ns: "Global" })}
              noOptionsLabel={t("There are no items to display", {
                ns: "Global",
              })}
              options={(categoryOptions.categoryIds ?? []).map(id => ({
                value: id,
                label: categoryOptions.categoryLabels[id] ?? "",
                counter: categoryOptions.categoryCounters[id],
              }))}
              value={values.categories?.[0] ?? null}
              onChange={async newValue => {
                await setFieldValue("categories", newValue ? [newValue] : null);
              }}
              filterOptions={categoryOptions?.filterCategoryOptions}
              disabled={!isNil(fixedFilters?.categories)}
              popoverWidth="xl"
              clearable
            />
          ) : null
        ) : null,

        (!hideTypeOfProjects ||
          (values.typeOfProjects && values.typeOfProjects.length > 0)) &&
        typeOfProjectOptions.typeOfProjectValues &&
        typeOfProjectOptions.typeOfProjectValues.length > 0 ? (
          viewType === "inputs" ? (
            <Autocomplete
              key="typeOfProjects-input"
              inputLabel={t("Type of project", { ns: "Templates" })}
              options={typeOfProjectOptions?.typeOfProjectValues ?? []}
              getOptionLabel={option =>
                typeOfProjectOptions?.typeOfProjectLabels[option] ?? ""
              }
              value={values.typeOfProjects?.[0] ?? null}
              onChange={async newValue => {
                await setFieldValue(
                  "typeOfProjects",
                  newValue ? [newValue] : null
                );
              }}
              filterOptions={
                typeOfProjectOptions?.filterTypeOfProjectStringOptions
              }
              renderOption={(props, option, { selected }) => (
                <li
                  {...props}
                  style={{
                    ...props.style,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>
                    {typeOfProjectOptions.typeOfProjectLabels[option] ?? ""}
                  </span>
                  {typeOfProjectOptions.typeOfProjectCounters[option] !==
                  undefined ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "grey.600", ml: 1 }}
                    >
                      {getFormattedNumber(
                        typeOfProjectOptions.typeOfProjectCounters[option],
                        language
                      )}
                    </Typography>
                  ) : null}
                </li>
              )}
              disabled={!isNil(fixedFilters?.typeOfProjects)}
            />
          ) : viewType === "buttons" ? (
            <ButtonSelect
              key="typeOfProjects-button"
              label={t("Type of project", { ns: "Templates" })}
              searchLabel={t("Search", { ns: "Global" })}
              noOptionsLabel={t("There are no items to display", {
                ns: "Global",
              })}
              options={(typeOfProjectOptions.typeOfProjectValues ?? []).map(
                value => ({
                  value: value,
                  label: typeOfProjectOptions.typeOfProjectLabels[value] ?? "",
                  counter: typeOfProjectOptions.typeOfProjectCounters[value],
                })
              )}
              value={values.typeOfProjects?.[0] ?? null}
              onChange={async newValue => {
                await setFieldValue(
                  "typeOfProjects",
                  newValue ? [newValue] : null
                );
              }}
              filterOptions={typeOfProjectOptions?.filterTypeOfProjectOptions}
              disabled={!isNil(fixedFilters?.typeOfProjects)}
              popoverWidth="lg"
              clearable
            />
          ) : null
        ) : null,

        (!hideTypeOfTemplates ||
          (values.typeOfTemplates && values.typeOfTemplates.length > 0)) &&
        typeOfTemplateOptions.typeOfTemplateValues &&
        typeOfTemplateOptions.typeOfTemplateValues.length > 0 ? (
          viewType === "inputs" ? (
            <Autocomplete
              key="typeOfTemplates-input"
              inputLabel={t("Type of template", { ns: "Templates" })}
              options={typeOfTemplateOptions?.typeOfTemplateValues ?? []}
              getOptionLabel={option =>
                typeOfTemplateOptions?.typeOfTemplateLabels[option] ?? ""
              }
              value={values.typeOfTemplates?.[0] ?? null}
              onChange={async newValue => {
                await setFieldValue(
                  "typeOfTemplates",
                  newValue ? [newValue] : null
                );
              }}
              filterOptions={
                typeOfTemplateOptions?.filterTypeOfTemplateStringOptions
              }
              renderOption={(props, option, { selected }) => (
                <li
                  {...props}
                  style={{
                    ...props.style,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>
                    {typeOfTemplateOptions.typeOfTemplateLabels[option] ?? ""}
                  </span>
                  {typeOfTemplateOptions.typeOfTemplateCounters[option] !==
                  undefined ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "grey.600", ml: 1 }}
                    >
                      {getFormattedNumber(
                        typeOfTemplateOptions.typeOfTemplateCounters[option],
                        language
                      )}
                    </Typography>
                  ) : null}
                </li>
              )}
              disabled={!isNil(fixedFilters?.typeOfTemplates)}
            />
          ) : viewType === "buttons" ? (
            <ButtonSelect
              key="typeOfTemplates-button"
              label={t("Type of template", { ns: "Templates" })}
              searchLabel={t("Search", { ns: "Global" })}
              noOptionsLabel={t("There are no items to display", {
                ns: "Global",
              })}
              options={(typeOfTemplateOptions.typeOfTemplateValues ?? []).map(
                value => ({
                  value: value,
                  label:
                    typeOfTemplateOptions.typeOfTemplateLabels[value] ?? "",
                  counter: typeOfTemplateOptions.typeOfTemplateCounters[value],
                })
              )}
              value={values.typeOfTemplates?.[0] ?? null}
              onChange={async newValue => {
                await setFieldValue(
                  "typeOfTemplates",
                  newValue ? [newValue] : null
                );
              }}
              filterOptions={typeOfTemplateOptions?.filterTypeOfTemplateOptions}
              disabled={!isNil(fixedFilters?.typeOfTemplates)}
              popoverWidth="lg"
              clearable
            />
          ) : null
        ) : null,
      ].filter(notNull),
    [
      hideTemplateTypes,
      values.templateTypeIds,
      values.categories,
      values.typeOfProjects,
      values.typeOfTemplates,
      templateTypeOptions.templateTypeIds,
      templateTypeOptions?.filterTemplateTypeStringOptions,
      templateTypeOptions?.filterTemplateTypeOptions,
      templateTypeOptions.templateTypeLabels,
      templateTypeOptions.templateTypeCounters,
      viewType,
      t,
      fixedFilters?.templateTypeIds,
      fixedFilters?.categories,
      fixedFilters?.typeOfProjects,
      fixedFilters?.typeOfTemplates,
      hideCategories,
      categoryOptions.categoryIds,
      categoryOptions?.filterCategoryStringOptions,
      categoryOptions?.filterCategoryOptions,
      categoryOptions.categoryLabels,
      categoryOptions.categoryCounters,
      hideTypeOfProjects,
      typeOfProjectOptions.typeOfProjectValues,
      typeOfProjectOptions?.filterTypeOfProjectStringOptions,
      typeOfProjectOptions?.filterTypeOfProjectOptions,
      typeOfProjectOptions.typeOfProjectLabels,
      typeOfProjectOptions.typeOfProjectCounters,
      hideTypeOfTemplates,
      typeOfTemplateOptions.typeOfTemplateValues,
      typeOfTemplateOptions?.filterTypeOfTemplateStringOptions,
      typeOfTemplateOptions?.filterTypeOfTemplateOptions,
      typeOfTemplateOptions.typeOfTemplateLabels,
      typeOfTemplateOptions.typeOfTemplateCounters,
      setFieldValue,
      language,
    ]
  );

  const propertyFields = React.useMemo((): React.ReactElement[] => {
    const options = {
      t,
      language,
      values: {
        properties: values.properties,
      },
      setFieldValue,
      viewType,
      fixedFilters,
    };
    return propertiesOptions.properties &&
      propertiesOptions.properties.length > 0
      ? propertiesOptions.properties
          .map(property => renderPropertyInput(property, options))
          .filter(notNull)
      : [];
  }, [
    t,
    language,
    values.properties,
    setFieldValue,
    viewType,
    fixedFilters,
    propertiesOptions.properties,
  ]);

  return {
    nonPropertyFields,
    propertyFields,
    isLoading: availableFiltersQuery.loading,
  };
}

export const templateFiltersValidationSchema = Yup.object().shape({
  typeOfProjects: Yup.array(Yup.string().required()).nullable(),
  typeOfTemplates: Yup.array(Yup.string().required()).nullable(),
  categories: Yup.array(Yup.string().required()).nullable(),
  templateTypeIds: Yup.array(Yup.string().required()).nullable(),
  properties: Yup.array()
    .of(
      Yup.object({
        key: Yup.string().required(),
        textFilter: Yup.object({
          operatorText: Yup.string().oneOf(["eq"]),
          valueText: Yup.string(),
        })
          .nullable()
          .optional(),
        textInFilter: Yup.object({
          operatorTextIn: Yup.string().oneOf(["in"]),
          valueTextIn: Yup.array(Yup.string().required()),
        })
          .nullable()
          .optional(),
        boolFilter: Yup.object({
          operatorBool: Yup.string().oneOf(["eq"]),
          valueBool: Yup.bool(),
        })
          .nullable()
          .optional(),
        boolInFilter: Yup.object({
          operatorBoolIn: Yup.string().oneOf(["in"]),
          valueBoolIn: Yup.array(Yup.bool().required()),
        })
          .nullable()
          .optional(),
        numberFilter: Yup.object({
          operatorNumber: Yup.string().oneOf(["eq", "lt", "lte", "gt", "gte"]),
          valueNumber: Yup.bool(),
        })
          .nullable()
          .optional(),
        numberInFilter: Yup.object({
          operatorNumberIn: Yup.string().oneOf(["in"]),
          valueNumberIn: Yup.array(Yup.number().required()),
        })
          .nullable()
          .optional(),
        numberBetweenFilter: Yup.object({
          operatorNumberBetween: Yup.string().oneOf(["between"]),
          valueNumberBetween: Yup.object({
            min: Yup.number(),
            max: Yup.number(),
          }),
        })
          .nullable()
          .optional(),
        textArrayOfFilter: Yup.object({
          operatorTextArrayOf: Yup.string().oneOf(["allOf", "anyOf"]),
          valueTextArrayOf: Yup.array(Yup.string().required()),
        })
          .nullable()
          .optional(),
        numberArrayOfFilter: Yup.object({
          operatorNumberArrayOf: Yup.string().oneOf(["allOf", "anyOf"]),
          valueNumberArrayOf: Yup.array(Yup.number().required()),
        })
          .nullable()
          .optional(),
      })
    )
    .nullable(),
});

export const getTemplateFormValues = <T extends TemplateFiltersFormValues>(
  values: T
): TemplateFiltersFormValues =>
  pick(
    values,
    "templateTypeIds",
    "categories",
    "properties",
    "typeOfTemplates",
    "typeOfProjects"
  );

export const templateFiltersDefaultValue: TemplateFiltersFormValues = {
  typeOfTemplates: null,
  typeOfProjects: null,
  categories: null,
  templateTypeIds: null,
  properties: null,
};

function renderPropertyInput(
  property: TemplateProperty,
  {
    t,
    language,
    setFieldValue,
    values,
    viewType,
    fixedFilters,
  }: {
    t: TFunction<"Global">;
    language: string;
    setFieldValue: FormikProps<TemplateFiltersFormValues>["setFieldValue"];
    values: Pick<TemplateFiltersFormValues, "properties">;
    viewType: FiltersViewType;
    fixedFilters?: Partial<TemplateFiltersFormValues>;
  }
): React.ReactElement | null {
  const propertyFilter = values.properties?.find(
    propertyFilter => property.key === propertyFilter.key
  );

  switch (property.type) {
    case "bool": {
      const options = property.values.map(v => ({
        label:
          v.value === true
            ? t("Yes", { ns: "Global" })
            : t("No", { ns: "Global" }),
        value: v.value === true ? "true" : "false",
        counter: v.count,
      }));

      const value = propertyFilter?.boolFilter
        ? propertyFilter?.boolFilter.valueBool === true
          ? "true"
          : "false"
        : "";
      const onChange = (newValue: string | null) => {
        const newPropertyFilters =
          values.properties?.filter(
            propertyFilter => property.key !== propertyFilter.key
          ) ?? [];
        if (newValue === "true" || newValue === "false") {
          newPropertyFilters.push({
            key: property.key,
            boolFilter: {
              operatorBool: "eq",
              valueBool: newValue === "true" ? true : false,
            },
          });
        }
        setFieldValue("properties", newPropertyFilters);
      };
      return viewType === "inputs" ? (
        <Select
          key={`${property.key}-input`}
          label={property.label}
          options={options ?? []}
          value={value}
          onChange={onChange}
          clearable
          onClear={() => onChange(null)}
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : viewType === "buttons" ? (
        <ButtonSelect
          key={`${property.key}-button`}
          label={property.label}
          searchLabel={t("Search", { ns: "Global" })}
          noOptionsLabel={t("There are no items to display", {
            ns: "Global",
          })}
          options={options ?? []}
          value={value || null}
          onChange={onChange}
          popoverWidth="md"
          clearable
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : null;
    }
    case "number": {
      const options = property.values.map(v => ({
        label: getFormattedNumber(v.value, language),
        value: v.value.toString(),
        counter: v.count,
      }));

      const value = propertyFilter?.numberBetweenFilter
        ? property.values
            .filter(
              v =>
                v.value >=
                  propertyFilter.numberBetweenFilter!.valueNumberBetween.min &&
                v.value <=
                  propertyFilter.numberBetweenFilter!.valueNumberBetween.max
            )
            .map(v => v.value.toString())
        : propertyFilter?.numberInFilter
          ? propertyFilter.numberInFilter.valueNumberIn.map(v => v.toString())
          : propertyFilter?.numberFilter
            ? propertyFilter.numberFilter.operatorNumber === "eq"
              ? [propertyFilter.numberFilter.valueNumber.toString()]
              : propertyFilter.numberFilter.operatorNumber === "lt" ||
                  propertyFilter.numberFilter.operatorNumber === "lte"
                ? property.values
                    .filter(
                      v => v.value <= propertyFilter.numberFilter!.valueNumber
                    )
                    .map(v => v.value.toString())
                : propertyFilter.numberFilter.operatorNumber === "gt" ||
                    propertyFilter.numberFilter.operatorNumber === "gte"
                  ? property.values
                      .filter(
                        v => v.value >= propertyFilter.numberFilter!.valueNumber
                      )
                      .map(v => v.value.toString())
                  : assertNever(propertyFilter.numberFilter.operatorNumber)
            : [];
      const onChange = (newValue: string[]) => {
        const newPropertyFilters =
          values.properties?.filter(
            propertyFilter => property.key !== propertyFilter.key
          ) ?? [];
        if (newValue.length > 0) {
          newPropertyFilters.push({
            key: property.key,
            numberInFilter: {
              operatorNumberIn: "in",
              valueNumberIn: newValue.map(v => parseFloat(v)),
            },
          });
        }
        setFieldValue("properties", newPropertyFilters);
      };
      return viewType === "inputs" ? (
        <SelectMultiple
          key={`${property.key}-input`}
          label={property.label}
          options={options ?? []}
          value={value}
          onChange={onChange}
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : viewType === "buttons" ? (
        <ButtonSelectMultiple
          key={`${property.key}-button`}
          label={property.label}
          searchLabel={t("Search", { ns: "Global" })}
          noOptionsLabel={t("There are no items to display", {
            ns: "Global",
          })}
          options={options ?? []}
          value={value}
          onChange={onChange}
          popoverWidth="md"
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : null;
    }
    case "number_array": {
      const options = property.values.map(v => ({
        label: getFormattedNumber(v.value, language),
        value: v.value.toString(),
        counter: v.count,
      }));
      const value = propertyFilter?.numberArrayOfFilter
        ? propertyFilter.numberArrayOfFilter.valueNumberArrayOf.map(v =>
            v.toString()
          )
        : [];
      const onChange = (newValue: string[]) => {
        const newPropertyFilters =
          values.properties?.filter(
            propertyFilter => property.key !== propertyFilter.key
          ) ?? [];
        if (newValue.length > 0) {
          newPropertyFilters.push({
            key: property.key,
            numberArrayOfFilter: {
              operatorNumberArrayOf: "anyOf",
              valueNumberArrayOf: newValue.map(v => parseFloat(v)),
            },
          });
        }
        setFieldValue("properties", newPropertyFilters);
      };
      return viewType === "inputs" ? (
        <SelectMultiple
          key={`${property.key}-input`}
          label={property.label}
          options={options ?? []}
          value={value}
          onChange={onChange}
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : viewType === "buttons" ? (
        <ButtonSelectMultiple
          key={`${property.key}-button`}
          label={property.label}
          searchLabel={t("Search", { ns: "Global" })}
          noOptionsLabel={t("There are no items to display", {
            ns: "Global",
          })}
          options={options ?? []}
          value={value}
          onChange={onChange}
          popoverWidth="md"
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : null;
    }
    case "text": {
      const options = property.values.map(v => ({
        label: v.value,
        value: v.value,
        counter: v.count,
      }));
      const value = propertyFilter?.textFilter
        ? [propertyFilter.textFilter.valueText]
        : propertyFilter?.textInFilter
          ? propertyFilter.textInFilter.valueTextIn
          : [];
      const onChange = (newValue: string[]) => {
        const newPropertyFilters =
          values.properties?.filter(
            propertyFilter => property.key !== propertyFilter.key
          ) ?? [];
        if (newValue.length > 0) {
          newPropertyFilters.push({
            key: property.key,
            textInFilter: { operatorTextIn: "in", valueTextIn: newValue },
          });
        }
        setFieldValue("properties", newPropertyFilters);
      };
      return viewType === "inputs" ? (
        <SelectMultiple
          key={`${property.key}-input`}
          label={property.label}
          options={options ?? []}
          value={value}
          onChange={onChange}
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : viewType === "buttons" ? (
        <ButtonSelectMultiple
          key={`${property.key}-button`}
          label={property.label}
          searchLabel={t("Search", { ns: "Global" })}
          noOptionsLabel={t("There are no items to display", {
            ns: "Global",
          })}
          options={options ?? []}
          value={value}
          onChange={onChange}
          popoverWidth="lg"
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : null;
    }
    case "text_array": {
      const options = property.values.map(v => ({
        label: v.label,
        value: v.value,
        counter: v.count,
      }));
      const value = propertyFilter?.textArrayOfFilter
        ? propertyFilter.textArrayOfFilter.valueTextArrayOf
        : [];
      const onChange = (newValue: string[]) => {
        const newPropertyFilters =
          values.properties?.filter(
            propertyFilter => property.key !== propertyFilter.key
          ) ?? [];
        if (newValue.length > 0) {
          newPropertyFilters.push({
            key: property.key,
            textArrayOfFilter: {
              operatorTextArrayOf: "anyOf",
              valueTextArrayOf: newValue,
            },
          });
        }
        setFieldValue("properties", newPropertyFilters);
      };
      return viewType === "inputs" ? (
        <SelectMultiple
          key={`${property.key}-input`}
          label={property.label}
          options={options ?? []}
          value={value}
          onChange={onChange}
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : viewType === "buttons" ? (
        <ButtonSelectMultiple
          key={`${property.key}-button`}
          label={property.label}
          searchLabel={t("Search", { ns: "Global" })}
          noOptionsLabel={t("There are no items to display", {
            ns: "Global",
          })}
          options={options ?? []}
          value={value}
          onChange={onChange}
          popoverWidth="lg"
          disabled={!isNil(fixedFilters?.properties)}
        />
      ) : null;
    }
    default:
      return assertNever(property);
  }
}

export function isAnyFilterSet(filters: TemplateFiltersFormValues) {
  return (
    isArrayFilterSet(filters.templateTypeIds) ||
    isArrayFilterSet(filters.properties)
  );
}

function isArrayFilterSet(filter: any[] | null) {
  return filter && filter.length > 0;
}
